import React, { useState, useRef } from "react";
import { Formik, Form } from "formik";
import swal from "sweetalert";
import { usePost } from "seed/api";
import ExcelField from "components/util/helpers/fields/ExcelField";
import { downloadFile } from "components/util/file_util";
import Table from "components/collections/Table";
import l from "components/util/l";

const ImportForm = ({
  activeCollection,
  primaryKey = null,
  columns = [],
  isSingleImport = false,
  modifyStructure = false,
  onClickImport = () => { },
  onClose = () => { }
}) => {

  let filteredColumns = columns.filter((c) => !c.internal);
  if (primaryKey) {
    filteredColumns = filteredColumns.sort((a, b) => {
      if (a.name === primaryKey) return -1;
      if (b.name === primaryKey) return 1;
      return 0;
    });
  }

  const excelFileRef = useRef();
  const [importedData, setImportedData] = useState(null);
  const [overrideColumns, setOverrideColumns] = useState(false);

  const [callGetExportData] = usePost("/collections/export_data", {
    onCompleted: (data) =>
      downloadFile(data.url, null, data.name)
  });

  const [callValidateData, reqValidateData] = usePost("/collections/validate_import", {
    onCompleted: (data) => {
      if (data.logs.length == 0) {
        setImportedData(data);
      }
      else {
        if (data.logs.length > 0)
          excelFileRef.current.setStatus({
            type: "danger",
            message: l("Error al cargar el archivo%CA095"),
            logs: data.logs
          });
        else
          excelFileRef.current.setStatus({
            type: "success",
            message: l("Archivo cargado correctamente%CA096"),
            logs: data.logs
          });
      }
    }
  });

  const loadDataToValidate = async (data) => {

    if (!data) return;
    let { rows, columns } = data;

    let hasSameColumns = true;

    for (let i = 0; i < Math.max(columns.length, filteredColumns.length); i++) {

      const importedColumn = columns[i];
      const filteredColumn = filteredColumns[i];

      if (!importedColumn || !filteredColumn) {
        hasSameColumns = false;
        break;
      }

      if (importedColumn?.label != filteredColumn?.label) {
        hasSameColumns = false;
        break;
      }

    }

    if (!hasSameColumns) {
      if (modifyStructure) {
        swal({
          title: l("Actualizar columnas%CA099"),
          icon: "warning",
          text: l("Se encontraron columnas diferentes a las de la tabla. ¿Deseas actualizar las columnas de la tabla?%CA100"),
          buttons: {
            confirm: {
              text: l("Aceptar%GA020"),
              className: "swal-button swal-button--cancel btn-success",
            },
            cancel: l("Conservar columnas%CA157")
          },
        }).then((response) => {
          if (response) {
            setOverrideColumns(false);
            callValidateData({
              collection_id: activeCollection.id,
              primary_key: columns[0]?.name || null,
              data: rows,
              schema: columns,
              columns: columns,
              override_columns: true
            });
          }
          else {
            setOverrideColumns(true);
          }
        });
      }
      else {
        swal({
          title: l("Las columnas no coinciden%CA168"),
          icon: "error",
          text: l("El archivo tiene columnas diferentes a las de la tabla. Por favor, descarga la plantilla y asegúrate de que las columnas coincidan.%CA169"),
          button: {
            text: l("Descargar plantilla%CA170"),
            className: "swal-button btn-primary",
          },
        }).then((response) => {
          setImportedData(null);
          if (response)
            onClickDownloadTemplate();
        });
      }
    }
    else {
      callValidateData({
        collection_id: activeCollection.id,
        primary_key: primaryKey,
        schema: filteredColumns,
        data: rows.map((r) => {
          let newRow = {};
          filteredColumns.forEach((col, index) => {
            newRow[col.name] = r.data[columns[index].name];
          });
          return { data: newRow };
        }),
      });
    }
  }

  const onClickDownloadTemplate = () =>
    callGetExportData({
      collection_id: activeCollection.id,
    });

  const _onClickImport = () => {
    onClickImport(importedData);
    onClose();
  }

  const isLoading = reqValidateData.loading;

  const created = importedData?.summary?.created || 0;
  const updated = importedData?.summary?.updated || 0;
  const deleted = importedData?.summary?.deleted || 0;
  const hasChanges = (created + updated + deleted) > 0;

  const displayColumns = importedData?.columns?.map(c => ({ ...c, type: "string" })) || [];
  const displayRows = importedData?.rows?.filter((r) => r.action != 'none') || [];

  return <div className="card">

    <div class="card-header">
      <h3 class="mb-0">
        {l("Cargar excel%CA101")}
      </h3>
    </div>
    <div class="card-body py-4 px-6">

      <Formik
        initialValues={{}}
        onSubmit={_onClickImport}
      >
        {({ values, setFieldValue }) =>
          <Form>

            <ExcelField
              ref={excelFileRef}
              rowLimit={1000}
              referenceColumns={filteredColumns}
              overrideColumns={overrideColumns}
              isSingleImport={isSingleImport}
              showPreview={false}
              onLoadFile={loadDataToValidate}
              onDownloadTemplate={onClickDownloadTemplate}
            />

            {
              hasChanges &&
              <div className="w-100 overflow-auto mt-2 mb-2">
                <Table
                  showExport={false}
                  showFilters={false}
                  showPagination={false}
                  showForm={false}
                  showImport={false}
                  showSearch={false}
                  isReadOnly={true}
                  structure={displayColumns}
                  values={displayRows}
                />
              </div>
            }

            {
              created > 0 &&
              <div class="text-success">
                <i class="fa fa-check-circle mr-2"></i>
                {l("Se crearán:%CA162")} {created} {l("registro(s)%CA165")}
              </div>
            }

            {
              updated > 0 &&
              <div class="text-warning">
                <i class="fa fa-exclamation-triangle mr-2"></i>
                {l("Se actualizarán:%CA163")} {updated} {l("registro(s)%CA165")}
              </div>
            }

            {
              deleted > 0 &&
              <div class="text-danger">
                <i class="fa fa-trash mr-2"></i>
                {l("Se eliminarán:%CA164")} {deleted} {l("registro(s)%CA165")}
              </div>
            }

            {
              !hasChanges && importedData &&
              <div class="text-info">
                <i class="fa fa-info-circle mr-2"></i>
                {l("Sin cambios a realizar%CA166")}
              </div>
            }

            <button
              type="submit"
              class={`btn btn-block mt-4 
                ${excelFileRef?.current?.status?.logs?.reduce((acc, log) => acc || log.type == "error", false)?.length > 0
                  ? "btn-warning" : "btn-primary"}
              `}
              disabled={isLoading || !importedData}
            >
              {
                isLoading && <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              }
              {
                !isLoading && <><i class="fa fa-upload mr-2"></i> {l("Importar%CA107")} </>
              }
            </button>

          </Form>

        }
      </Formik>

    </div>

  </div>;

}

export default ImportForm;